import React, { useMemo, useState } from 'react';
import { RefreshCw, Info } from 'lucide-react';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { calcularMetricasFaturamento } from '../../../src/lib/financeiro/metricasFaturamento';
import { useDashboardFaturamentoData } from './hooks/useDashboardFaturamentoData';
import {
  calcularJanelaEmissao,
  emissaoNaJanela,
  rotuloPeriodoEmissao,
  OPCOES_PERIODO_EMISSAO,
  type PeriodoEmissao,
} from './lib/periodoEmissao';
import KpiCardsFaturamento from './components/KpiCardsFaturamento';
import RankingMotivos from './components/RankingMotivos';
import TabelaPorUsuario from './components/TabelaPorUsuario';
import FaturasForaDoPrazoModal from './components/FaturasForaDoPrazoModal';

const selectCls =
  'px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ' +
  'text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40 ' +
  'dark:[color-scheme:dark]';

/**
 * Seção "Faturamento" — ritmo de emissão dos boletos da Vigor.
 *
 * Renderizada no fim do Resumo Financeiro (`/financeiro`, FinanceiroLayout), logo
 * abaixo do "Emissões por Usuário": aquele painel conta QUANTO cada pessoa emitiu,
 * este responde o quão dentro do prazo isso foi feito. Não é tela própria — não
 * tem rota nem entrada de menu.
 *
 * Filtros próprios, e não os do topo do Resumo Financeiro: aquele seletor recorta
 * por `mes_referencia` (grão de mês, sobre a fatura), enquanto aqui o corte é por
 * `emitido_em` (um instante, sobre o trabalho de quem emitiu) — "últimos 30 dias"
 * é a pergunta que o Faturamento faz sobre o próprio ritmo e não cabe num seletor
 * de mês de referência.
 *
 * ⚠️ Acesso: `/financeiro/*` só bloqueia `motoboy` (App.tsx), então esta seção é
 * visível a todo mundo que abre o Resumo Financeiro — inclusive usuários de
 * empresa parceira. Como ela mostra desempenho nominal do time interno, veja a
 * observação no fim: se isso não for desejado, o recorte tem de vir de fora.
 */
const SecaoFaturamento: React.FC = () => {
  const { emissoes, timeFaturamento, loading, erro, ultimaAtualizacao, refetch } = useDashboardFaturamentoData();

  // Default de 30 dias, e não "todo o histórico" como no Painel Executivo de
  // inadimplência: lá a métrica é ESTOQUE (dívida antiga não some com o tempo),
  // aqui é FLUXO — a média de todos os meses esconderia uma piora recente.
  const [periodo, setPeriodo] = useState<PeriodoEmissao>('ultimos30');
  const [usuario, setUsuario] = useState<string>('todos');
  const [verForaDoPrazo, setVerForaDoPrazo] = useState(false);

  const hoje = useMemo(() => new Date(), []);
  const janela = useMemo(() => calcularJanelaEmissao(periodo, hoje), [periodo, hoje]);

  const noPeriodo = useMemo(
    () => emissoes.filter((e) => emissaoNaJanela(e.emitidoEm, janela)),
    [emissoes, janela],
  );

  const metricas = useMemo(() => {
    const filtradas = usuario === 'todos'
      ? noPeriodo
      : noPeriodo.filter((e) => e.emitidoPorNome === usuario);
    // O recorte pelo time e REGRA, nao filtro: o dashboard existe para medir o
    // departamento de Faturamento, e emissao feita por quem esta fora dele nao
    // descreve o processo que se quer acompanhar.
    return calcularMetricasFaturamento(filtradas, timeFaturamento);
  }, [noPeriodo, usuario, timeFaturamento]);

  // Quem emitiu no período mas está fora do time: o número aparece, para o
  // recorte "só Faturamento" não esconder trabalho em silêncio.
  const foraDoTime = useMemo(() => {
    if (timeFaturamento.length === 0) return 0;
    const doTime = new Set(timeFaturamento.map((n) => n.trim().toLowerCase()));
    return new Set(
      noPeriodo
        .filter((e) => e.ordem === 0 && !doTime.has((e.emitidoPorNome || '').trim().toLowerCase()))
        .map((e) => e.emitidoPorNome),
    ).size;
  }, [noPeriodo, timeFaturamento]);

  const opcoesUsuario = useMemo(() => {
    // Sem time cadastrado nao ha recorte possivel; cair para quem de fato emitiu
    // evita um seletor vazio (o aviso no rodape explica o que aconteceu).
    const base = timeFaturamento.length > 0
      ? timeFaturamento
      : Array.from(new Set(noPeriodo.map((e) => e.emitidoPorNome))).filter(Boolean);
    return base.slice().sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [timeFaturamento, noPeriodo]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-bold text-gray-800 dark:text-white">Faturamento</h2>
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            ritmo de emissão dos boletos da Vigor · só o departamento de Faturamento
            {ultimaAtualizacao && ` · ${ultimaAtualizacao.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="filtro-periodo-faturamento" className="sr-only">Período do faturamento</label>
          <select
            id="filtro-periodo-faturamento"
            className={selectCls}
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value as PeriodoEmissao)}
          >
            {OPCOES_PERIODO_EMISSAO.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <label htmlFor="filtro-usuario-faturamento" className="sr-only">Responsável pela emissão</label>
          <select
            id="filtro-usuario-faturamento"
            className={selectCls}
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          >
            <option value="todos">Todo o departamento</option>
            {opcoesUsuario.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>

          <button
            onClick={refetch}
            className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            title="Atualizar"
            aria-label="Atualizar dados do faturamento"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {erro ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
          <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>
          <button onClick={refetch} className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">
            Tentar novamente
          </button>
        </div>
      ) : loading ? (
        // Spinner só desta seção: as outras da página carregam sozinhas e não
        // podem ficar esperando esta (a carteira inteira de faturas é pesada).
        <div className="py-12"><LoadingSpinner label="Carregando faturamento..." size="sm" /></div>
      ) : (
        <>
          <KpiCardsFaturamento
            metricas={metricas}
            rotuloPeriodo={rotuloPeriodoEmissao(periodo)}
            onVerForaDoPrazo={() => setVerForaDoPrazo(true)}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TabelaPorUsuario metricas={metricas} />
            <RankingMotivos ranking={metricas.rankingMotivos} semMotivo={metricas.foraDoPrazoSemMotivo} />
          </div>

          <div className="flex items-start gap-2 rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-[11px] text-gray-500 dark:text-gray-400">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <p>
                O prazo padrão termina 5 dias úteis (7 corridos) antes do vencimento da fatura da
                Equatorial. Boleto avulso e reemissão não entram na conta — só a primeira emissão
                de cada fatura.
              </p>
              {metricas.acimaDoCorte > 0 && (
                <p>
                  {metricas.acimaDoCorte}{' '}
                  {metricas.acimaDoCorte === 1
                    ? 'emissão passou de 45 dias de atraso e ficou fora da conta'
                    : 'emissões passaram de 45 dias de atraso e ficaram fora da conta'}
                  {' '}— nessa faixa é fatura antiga lançada retroativamente ou dívida renegociada,
                  não o boleto do mês. O trabalho segue no total de boletos faturados.
                </p>
              )}
              <p>
                O tempo médio conta da criação da fatura no sistema até a emissão; faturas sem essa
                data ficam fora da média, e o denominador aparece na tabela.
              </p>
              <p>
                Só entram emissões de quem tem departamento "Faturamento" no perfil
                {timeFaturamento.length > 0 && ` (${timeFaturamento.length} ${timeFaturamento.length === 1 ? 'pessoa' : 'pessoas'})`}.
                {foraDoTime > 0 && ` Outras ${foraDoTime} ${foraDoTime === 1 ? 'pessoa emitiu' : 'pessoas emitiram'} no período e ficam de fora destes números.`}
              </p>
              {timeFaturamento.length === 0 && (
                <p className="text-amber-600 dark:text-amber-400">
                  Nenhum perfil está com departamento "Faturamento" — sem esse cadastro não há
                  como recortar o time, e os números abaixo incluem todo mundo que emitiu.
                </p>
              )}
            </div>
          </div>

          <FaturasForaDoPrazoModal
            isOpen={verForaDoPrazo}
            onClose={() => setVerForaDoPrazo(false)}
            emissoes={metricas.emissoesForaDoPrazo}
            rotuloPeriodo={rotuloPeriodoEmissao(periodo)}
          />
        </>
      )}
    </div>
  );
};

export default SecaoFaturamento;
